
import * as _ from "lodash"
import Vue from "vue"
import * as am4core from "@amcharts/amcharts4/core"
import * as am4charts from "@amcharts/amcharts4/charts"
import {ColumnSeriesDataItem, XYChart} from "@amcharts/amcharts4/charts"
import Component from "vue-class-component"
import {cubejsApi} from "@/cube/cubejsApi"
import {chartTheme} from "@/ui/chartTheme"
import {QChip, QToggle} from "quasar"
import {dropShadowFilter} from "@/ui/chart/dropShadowFilter"
import type {Dimension, Measure} from "@/cube"
import {MemberSnapshotCube} from "@/cube"
import {Inject, Prop} from "vue-property-decorator"
import {removeDimensionSortPrefix} from "@/cube/removeDimensionSortPrefix"
import {theme} from "@/theme"
import {addDays, addMonths, format, parseISO} from "date-fns"
import {Campaign, CampaignService} from "@/CampaignService"
import {DateRange, DateRangeSelector} from "@/DateRangeSelector"
import {PlanService} from "@/superannuation/PlanService"
import {updateChangeRate} from "@/ui/chart/updateChangeRate"
import {dimensionSettings, measureSettings} from "@/cube/settings"
import {formatValue} from "@/utils/formatter"
import {DateRangeService} from "@/DateRangeService"

am4core.useTheme(chartTheme)

// FIXME remove zoom out button, broken doesn't update change period
@Component({components: {QChip, QToggle}})
export default class OverTimeChart extends Vue {
  @Prop({required: true})
  measure: Measure

  @Prop({required: true})
  dimensions?: Array<Dimension>

  @Prop()
  dateRangeSelector?: DateRangeSelector

  @Inject()
  campaignService: CampaignService

  @Inject()
  planService: PlanService

  @Inject()
  dateRangeService: DateRangeService

  private unwatchDateRange?: () => void
  private chart?: am4charts.XYChart
  private resultSet: any

  private isPercentualViewEnabled = false
  private isLoading = true

  private dontDisplayDimensionValues = ["None", "False", "No"]

  get baseQuery() {
    const dimensions = this.dimensions == null ? [] : this.dimensions

    return <const>{
      timeDimensions: [
        {
          dimension: MemberSnapshotCube._at,
          granularity: <const>"month",
        },
      ],
      measures: [this.measure],
      dimensions,
    }
  }

  get isStackable() {
    return !this.measure.includes("average") && !this.measure.includes("Score")
  }

  async created() {
    this.$watch(
      () => [this.planService.current, this.dateRangeSelector].join(),
      async () => {
        if (
          this.planService.current != null &&
          this.dateRangeSelector?.selected != null
        ) {
          this.watchQuery(this.planService.current.id)
        }
      },

      {immediate: true}
    )

    this.$watch(
      () => this.planService.current?.id,
      async () => {
        // Need to turn off watcher for date range so it doesn't fire while
        // a new dateRangeSelector is created
        this.unwatchDateRange?.()
        await this.dateRangeService.setPlan(this.planService.current!.id)
        this.resultSet = await this.runQuery(this.planService.current!.id)
        if (this.chart != null) {
          this.chart.dispose()
        }
        await this.createChart()
        this.unwatchDateRange = this.watchDateRange()
      }
    )

    this.$watch(
      () => this.planService.engagedOnly,
      async () => {
        this.resultSet = await this.runQuery(this.planService.current!.id)
        this.chart!.dispose()
        await this.createChart()
      }
    )
  }

  beforeDestroy() {
    if (this.chart != null) {
      this.chart.dispose()
    }
  }

  private watchQuery(planId: string) {
    this.$watch(
      () => this.baseQuery,
      async () => {
        this.isLoading = true

        this.resultSet = await this.runQuery(planId)
        this.isPercentualViewEnabled =
          this.resultSet.seriesNames().length > 1 && this.isStackable

        if (this.chart != null) {
          // TODO investigate replacing the chart so we can dispose it after the
          //  new chart is rendered
          this.chart.dispose()
        }
        await this.createChart()
        this.unwatchDateRange = this.watchDateRange()
      },
      {immediate: true}
    )
  }

  private watchDateRange() {
    return this.$watch(
      () => this.dateRangeSelector!.selected,
      (newDateRange?: DateRange) => {
        if (newDateRange) {
          this.applyDateRangeToChart(newDateRange)
        }
      },
      {deep: true}
    )
  }

  private applyDateRangeToChart(
    selectedDateRange: DateRange,
    shouldZoomDates = true,
    shouldUpdateChangeRate = true
  ) {
    if (shouldZoomDates) {
      const dateAxis = <am4charts.DateAxis>this.chart!.xAxes.values[0]
      dateAxis.zoomToDates(
        selectedDateRange.start,
        addMonths(selectedDateRange.end, 1),
        true
      )
    }

    if (shouldUpdateChangeRate) {
      updateChangeRate(
        this.chart!,
        selectedDateRange.start,
        selectedDateRange.end,
        this.dimensions![0],
        this.isPercentualViewEnabled
      )
    }
  }

  private async runQuery(planId: string) {
    const order = _.chain(this.baseQuery.dimensions)
      .map(dimension => ({
        [dimension]: "asc",
      }))
      .push({[MemberSnapshotCube._at]: "asc"})
      .fromPairs()
      .value()

    const filters = [
      {
        dimension: "Member.planId",
        operator: "equals",
        values: [planId],
      },
    ].concat(
      this.planService.engagedOnly
        ? [
            {
              dimension: "Member.engaged",
              operator: "equals",
              values: ["1"],
            },
          ]
        : []
    )

    const query = {...this.baseQuery, filters, order}
    console.log("Running query:", query)

    return cubejsApi.load(query)
  }

  private async onPercentualViewChange() {
    if (this.chart != null) {
      this.chart.dispose()
    }
    await this.createChart()
  }

  private async createChart() {
    const chart = am4core.create((<any>this.$refs).chart, am4charts.XYChart)
    chart.responsive.enabled = true
    chart.paddingTop = 0
    chart.tapToActivate = true
    console.log("resultSet", this.resultSet)

    chart.data = this.resultSet.chartPivot()

    console.log("chart.data", chart.data)

    // Create axes

    // Time dimension
    const dateAxis = new am4charts.DateAxis()
    chart.xAxes.push(dateAxis)

    const axisByMeasure = this.createMeasureAxes(chart)

    console.log("axisByMeasure", axisByMeasure)

    // Add scrollbar
    const xyChartScrollbar = new am4charts.XYChartScrollbar()

    chart.scrollbarX = xyChartScrollbar
    chart.scrollbarX.parent = chart.bottomAxesContainer

    // The order of the series will determine how they are stacked. We cannot
    // rely on the query order alone because when there's no value for a series
    // it will affect the order of the seriesNames
    const seriesNames = _.sortBy(this.resultSet.seriesNames(), "key")
    console.log("Sorted series names:", seriesNames)

    for (const dataSeries of seriesNames) {
      console.log("dataSeries", dataSeries)
      // TODO Revisit this
      let chartSeries: am4charts.ColumnSeries
      chartSeries = new am4charts.ColumnSeries()
      // FIXME find correct type?
      chartSeries.columns.template.width = am4core.percent(60)
      chartSeries.fillOpacity = 0.25
      chartSeries.strokeOpacity = 0.25

      const measureName = _.last<string>(dataSeries.key.split(", "))!

      chartSeries.name =
        this.baseQuery.dimensions.length > 0
          ? _.dropRight<string>(dataSeries.title.split(", "), 1)
              .map(removeDimensionSortPrefix)
              .join("\n")
          : `${<string>(
              this.resultSet.loadResponse.results[0].annotation.measures[
                measureName
              ].shortTitle
            )}`
      console.log("Measure name", measureName)

      chartSeries.yAxis = axisByMeasure.get(measureName)
      chartSeries.dataFields.valueY = dataSeries.key
      chartSeries.dataFields.dateX = "category"
      chartSeries.adapter.add("fill", (fill, target) => {
        const {color} = dimensionSettings(this.dimensions![0], target.name)
        return color === undefined ? fill : am4core.color(color)
      })
      chartSeries.adapter.add("tooltipText", (value, target) => {
        if (
          this.dontDisplayDimensionValues.includes(chartSeries.name) &&
          this.isPercentualViewEnabled
        ) {
          return ""
        }
        const valueY = formatValue(
          this.measure,
          (<ColumnSeriesDataItem>target.tooltipDataItem).valueY
        )
        return `{name} \n[bold font-size: 20]${valueY}[/] ({valueY.totalPercent.formatNumber('###.#')}%)`
      })

      if (this.isPercentualViewEnabled) {
        chartSeries.dataFields.valueYShow = "totalPercent"
        chartSeries.stacked = true

        if (this.dontDisplayDimensionValues.includes(chartSeries.name)) {
          chartSeries.fillOpacity = 0
          chartSeries.strokeOpacity = 0
          chartSeries.strokeWidth = 0
          chartSeries.hiddenInLegend = true
          chartSeries.tooltipText = ""
        }

        const columnSeries = <am4charts.ColumnSeries>chartSeries
        columnSeries.clustered = false
      }
      chart.series.push(chartSeries)

      chartSeries.filters.push(dropShadowFilter())
      xyChartScrollbar.series.push(chartSeries)
    }

    chart.xAxes.values.forEach(
      axis => (axis.renderer.grid.template.strokeDasharray = "3,3")
    )
    chart.yAxes.values.forEach(
      axis => (axis.renderer.grid.template.strokeDasharray = "3,3")
    )

    // Add cursor
    chart.cursor = new am4charts.XYCursor()
    chart.cursor.behavior = "zoomXY"

    // Add legend
    chart.legend = new am4charts.Legend()
    chart.legend.position = "top"
    chart.legend.paddingBottom = am4core.percent(5)

    // TODO this breaks the tooltips
    chart.bottomAxesContainer.zIndex = -1

    chart.events.on("shown", async () => {
      // Update the zoom as early as possible to avoid rendering the chart and
      // then zooming in. The change rate is not updated here because it renders
      // disabled label for some reason
      this.applyDateRangeToChart(this.dateRangeSelector!.selected!, true, false)
    })

    chart.events.on("ready", async () => {
      // Only start tracking once ready to avoid events no generated via
      // user interaction
      xyChartScrollbar.events.on("rangechanged", this.scrollRangeChanged)

      xyChartScrollbar.startGrip.events.on("dragstop", this.scrollRangeChanged)
      xyChartScrollbar.endGrip.events.on("dragstop", this.scrollRangeChanged)
      await this.addCampaigns(chart, xyChartScrollbar)

      this.applyDateRangeToChart(this.dateRangeSelector!.selected!, false, true)
      this.isLoading = false
    })

    this.chart = chart
  }

  private createMeasureAxes(chart: XYChart) {
    const axisByMeasure = new Map()

    for (const [measureName, measure] of Object.entries<any>(
      this.resultSet.loadResponse.results[0].annotation.measures
    )) {
      const valueAxis = chart.yAxes.push(new am4charts.ValueAxis())
      valueAxis.title.text = measure.shortTitle
      valueAxis.calculateTotals = true

      valueAxis.min = 0

      if (this.isPercentualViewEnabled) {
        valueAxis.title.text = `% ${valueAxis.title.text}`

        valueAxis.max = 100
        valueAxis.strictMinMax = true
      }

      const source = measureSettings[<Measure>measureName]?.valueAxis

      if (source != null) {
        Object.assign(valueAxis, source)
      }

      valueAxis.renderer.opposite = false
      valueAxis.renderer.grid.template.disabled = false
      valueAxis.renderer.grid.template.strokeDasharray = "3,3"
      valueAxis.renderer.labels.template.adapter.add("text", value =>
        value === undefined ? value : formatValue(this.measure, value, true)
      )
      axisByMeasure.set(measureName, valueAxis)
    }

    return axisByMeasure
  }

  private scrollRangeChanged(event: any) {
    console.log("rangechanged", event)
    const {startDataItem, endDataItem} = this.getDataItemsFromScrollbar()

    this.dateRangeSelector!.selected = {
      start: startDataItem.dateX,
      end: endDataItem.dateX,
    }
  }

  private getDataItemsFromScrollbar() {
    const dateAxis = <am4charts.DateAxis>this.chart!.xAxes.values[0]
    const xySeries = dateAxis.series.values[0]
    const seriesDataItemStart = dateAxis.getSeriesDataItem(
      xySeries,
      this.chart!.scrollbarX.start,
      true
    )

    const seriesDataItemEnd = dateAxis.getSeriesDataItem(
      xySeries,
      this.chart!.scrollbarX.end,
      true
    )

    console.log("DataItem", seriesDataItemStart.dateX)
    return {
      startDataItem: seriesDataItemStart,
      endDataItem: seriesDataItemEnd,
    }
  }

  private async addCampaigns(
    chart: am4charts.XYChart,
    xyChartScrollbar: am4charts.XYChartScrollbar
  ) {
    const campaigns = await this.campaignService.getCampaigns(
      this.planService.current!.id
    )

    console.log("Campaigns", campaigns)

    for (const campaign of campaigns) {
      // TODO can reuse the axisRange?
      this.createCampaignEvent(campaign, xyChartScrollbar.scrollbarChart)
      this.createCampaignEvent(campaign, chart)
    }

    // xyChartScrollbar.scrollbarChart.xAxes.values[0].axisRanges.copyFrom(
    //   dateAxis.axisRanges
    // )
  }

  private createCampaignEvent(campaign: Campaign, chart: am4charts.XYChart) {
    const campaignEvent = (<am4charts.DateAxis>(
      chart.xAxes.values[0]
    )).axisRanges.create()

    campaignEvent.date = parseISO(campaign.date)
    campaignEvent.endDate = addDays(campaignEvent.date, 5)

    campaignEvent.label.background.fill = am4core.color(theme().alt2Color)
    campaignEvent.label.background.fillOpacity = 0.4
    campaignEvent.label.fill = am4core.color("white")
    campaignEvent.label.fillOpacity = 1
    campaignEvent.label.fontWeight = "700"
    campaignEvent.label.inside = true
    campaignEvent.label.rotation = -90
    campaignEvent.label.valign = "middle"
    campaignEvent.label.textAlign = "middle"

    campaignEvent.label.text = campaign.type

    // noinspection JSSuspiciousNameCombination
    campaignEvent.label.width = chart.yAxes.values[0].measuredHeight
    campaignEvent.label.fontSize = campaignEvent.label.width * 0.055

    campaignEvent.label.tooltip = new am4core.Tooltip()
    const formattedDate = format(campaignEvent.date, "dd/MM/yyyy")
    campaignEvent.label.tooltipText = `Campaign: \n[bold]${campaign.type}[/]\n${formattedDate}\n${campaign.recipients} members`
    campaignEvent.label.interactionsEnabled = true
  }
}
